// Local event queue — canon Phase 4 (IndexedDB-backed; pending→sending→delivered;
// retry with exponential backoff; dedup by event_id; flush on size/interval/unload).
// Core logic, port-injected (QueueStore + Transport + Clock + Scheduler).
import type {
  Clock,
  Envelope,
  Logger,
  QueueStore,
  QueuedEvent,
  Scheduler,
  Transport,
} from './types';

export interface EventQueueOptions {
  batchSize: number;
  flushIntervalMs: number;
  maxRetries: number;
  backoffBaseMs: number;
}

export class EventQueue {
  private readonly seen = new Set<string>();
  private flushing = false;
  private cancelTimer: (() => void) | null = null;

  constructor(
    private readonly store: QueueStore,
    private readonly transport: Transport,
    private readonly clock: Clock,
    private readonly scheduler: Scheduler,
    private readonly logger: Logger,
    private readonly opts: EventQueueOptions,
  ) {}

  /** Load leftovers from a previous session, start the periodic flush, try once. */
  async init(): Promise<void> {
    const pending = await this.store.loadPending();
    for (const e of pending) this.seen.add(e.envelope.event_id);
    this.scheduleNext();
    await this.flush();
  }

  private scheduleNext(): void {
    this.cancelTimer?.();
    this.cancelTimer = this.scheduler.schedule(() => {
      void this.flush();
    }, this.opts.flushIntervalMs);
  }

  async enqueue(envelope: Envelope): Promise<void> {
    if (this.seen.has(envelope.event_id)) return; // client-side dedup (canon rule 15)
    this.seen.add(envelope.event_id);
    await this.store.put({ envelope, attempts: 0, nextAttemptAt: 0 });
    const pending = await this.store.loadPending();
    if (pending.length >= this.opts.batchSize) await this.flush();
  }

  async flush(opts: { beacon?: boolean } = {}): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      const now = this.clock.now();
      const batch: QueuedEvent[] = (await this.store.loadPending())
        .filter((e) => e.nextAttemptAt <= now)
        .slice(0, this.opts.batchSize);
      if (batch.length === 0) return;

      const delivered = await this.transport.send(
        batch.map((e) => e.envelope),
        opts,
      );

      if (delivered) {
        for (const e of batch) await this.store.remove(e.envelope.event_id);
      } else {
        for (const e of batch) await this.backoff(e, now);
      }
    } catch (err) {
      this.logger.warn('[fg-analytics] flush error', err);
    } finally {
      this.flushing = false;
      if (!opts.beacon) this.scheduleNext();
    }
  }

  private async backoff(event: QueuedEvent, now: number): Promise<void> {
    event.attempts += 1;
    if (event.attempts > this.opts.maxRetries) {
      this.logger.warn('[fg-analytics] dropping event after max retries', event.envelope.event_id);
      await this.store.remove(event.envelope.event_id);
      return;
    }
    event.nextAttemptAt = now + this.opts.backoffBaseMs * 2 ** (event.attempts - 1);
    await this.store.put(event);
  }

  stop(): void {
    this.cancelTimer?.();
    this.cancelTimer = null;
  }
}
